import { planSyncTasks, SyncTaskResult } from "../sync/syncEngine.js";
import { checkReactNative } from "../checks/reactNative.js";
import { installDependency } from "./packageInstaller.js";
import { analyzeNativeRequirements } from "./nativeAnalyzer.js";
import { generateRecommendations } from "./recommendationEngine.js";
import { PackageKnowledge } from "./packageRegistry.js";
import { findPackageJson } from "../utils/files.js";
import { TaskRunner } from "../core/taskRunner.js";
import { runLockfileValidationTask } from "../sync/tasks/lockfileValidationTask.js";
import { runPodInstallTask } from "../sync/tasks/podInstallTask.js";
import { runGradleSyncTask } from "../sync/tasks/gradleSyncTask.js";
import { runNativeDependencyTask } from "../sync/tasks/nativeDependencyTask.js";
import { runProjectValidationTask } from "../sync/tasks/projectValidationTask.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";

export interface InstallReport {
  packageName: string;
  success: boolean;
  packageManager: string;
  command: string;
  installDetails?: string;
  syncResults: SyncTaskResult[];
  knowledge: PackageKnowledge | null;
  recommendations: string[];
}

/**
 * Orchestrates validation checks, PM installs, native synchronization, and configuration advice compiler.
 */
export async function executeInstall(
  cwd: string,
  packageName: string,
  isDev: boolean,
  verbose: boolean,
  dryRun: boolean,
  json: boolean = false
): Promise<InstallReport> {
  const pmResolution = await resolvePackageManager(cwd, verbose);
  const pm = pmResolution.selected;
  const knowledge = analyzeNativeRequirements(packageName);
  const recommendations = generateRecommendations(packageName, knowledge);

  const report: InstallReport = {
    packageName,
    success: false,
    packageManager: pm,
    command: "",
    syncResults: [],
    knowledge,
    recommendations,
  };

  // Resolve command string for tracking
  let command = "";
  if (pm === "pnpm") {
    command = `pnpm add ${isDev ? "-D " : ""}${packageName}`;
  } else if (pm === "yarn") {
    command = `yarn add ${isDev ? "-D " : ""}${packageName}`;
  } else if (pm === "bun") {
    command = `bun add ${isDev ? "-d " : ""}${packageName}`;
  } else {
    command = `npm install ${isDev ? "--save-dev " : ""}${packageName}`;
  }
  report.command = command;

  if (dryRun) {
    report.success = true;
    report.installDetails = "Dry run: Package installation simulated.";
    return report;
  }

  // Create unified TaskRunner
  const runner = new TaskRunner({ verbose, json });

  // 1. Validation Task
  runner.addTask({
    id: "validate-project",
    title: "Validating project root context",
    abortOnFailure: true,
    execute: async () => {
      const packageJsonPath = findPackageJson(cwd);
      if (!packageJsonPath) {
        throw new Error("Aborted: package.json not found. Run command from within a React Native project.");
      }
      const validationResults = await checkReactNative(cwd);
      const rnMissing = validationResults.some((c) => c.name === "React Native" && c.status === "error");
      if (rnMissing) {
        throw new Error("Aborted: react-native dependency not found in package.json.");
      }
    },
  });

  // 2. Package Installation Task
  runner.addTask({
    id: "install-package",
    title: `Installing ${packageName}`,
    abortOnFailure: true,
    execute: async () => {
      const res = await installDependency(cwd, pm, packageName, isDev, verbose);
      if (!res.success) {
        throw new Error(res.details || "Package installation failed.");
      }
      report.installDetails = res.details;
    },
  });

  // 3. Planned sync tasks
  const plannedSync = planSyncTasks(cwd, "all");
  const filteredPlannedSync = plannedSync.filter((t) => t.id !== "install");

  const syncResults: SyncTaskResult[] = [];

  for (const planned of filteredPlannedSync) {
    runner.addTask({
      id: planned.id,
      title: planned.title,
      abortOnFailure: false,
      execute: async () => {
        let res: { success: boolean; details?: string; skipped?: boolean };
        switch (planned.id) {
          case "verify-lockfile":
            res = await runLockfileValidationTask(cwd);
            break;
          case "pods":
            res = await runPodInstallTask(cwd, verbose);
            break;
          case "gradle-sync":
            res = await runGradleSyncTask(cwd, verbose);
            break;
          case "dependencies-audit":
            res = await runNativeDependencyTask(cwd);
            break;
          case "project-validation":
            res = await runProjectValidationTask(cwd);
            break;
          default:
            res = { success: false, details: `Unknown task identifier: ${planned.id}` };
        }

        if (!res.success) {
          throw new Error(res.details || "Task failed");
        }

        syncResults.push({
          id: planned.id,
          title: planned.title,
          success: res.success,
          skipped: !!res.skipped,
          durationMs: 0,
          details: res.details,
        });
      },
    });
  }

  // Run all tasks
  const runnerResults = await runner.run();

  const validateRes = runnerResults.find((r) => r.id === "validate-project");
  const installRes = runnerResults.find((r) => r.id === "install-package");

  if (validateRes && !validateRes.success) {
    report.success = false;
    report.installDetails = validateRes.error;
    return report;
  }

  if (installRes && !installRes.success) {
    report.success = false;
    report.installDetails = installRes.error;
    return report;
  }

  report.success = installRes ? installRes.success : false;

  report.syncResults = syncResults.map((s) => {
    const matchedRunnerRes = runnerResults.find((rr) => rr.id === s.id);
    return {
      ...s,
      success: matchedRunnerRes ? matchedRunnerRes.success : false,
      skipped: matchedRunnerRes ? matchedRunnerRes.error === "Skipped due to prior failure." : s.skipped,
      durationMs: matchedRunnerRes ? matchedRunnerRes.durationMs : 0,
      details: matchedRunnerRes ? matchedRunnerRes.error || s.details : s.details,
    };
  });

  return report;
}
